const { pageYOffset, scrollY, pageXOffset, scrollX } = window;
const doc = document;
const w = window;

export const keyArrowLeft = "ArrowLeft";
export const keyArrowRight = "ArrowRight";
export const keyArrowUp = "ArrowUp";
export const keyArrowDown = "ArrowDown";
export const keySpace = "Space";
export const keyEnter = "Enter";

export const eventResize = "resize";
export const eventScroll = "scroll";
export const eventClick = "click";
export const eventBlur = "blur";
export const eventCanplay = "canplay";
export const eventHashChange = "hashchange";
export const eventLoad = "load";

export const eventKeydown = "keydown";
export const eventKeyup = "keyup";

export const eventTouchStart = "touchstart";
export const eventTouchMove = "touchmove";
export const eventTouchEnd = "touchend";
export const eventTouchCancel = "touchcancel";

export const eventPointerDown = "pointerdown";
export const eventPointerMove = "pointermove";
export const eventPointerUp = "pointerup";
export const eventPointerCancel = "pointercancel";
export const eventPointerOut = "pointerout";
export const eventPointerLeave = "pointerleave";

export const eventMouseDown = "mousedown";
export const eventMouseMove = "mousemove";
export const eventMouseUp = "mouseup";

export const eventBeforeUnload = "beforeunload";

export const eventDragStart = "dragstart";

export const eventPrefOn = "on";
export const eventTypePointer = 1;
export const eventTypeTouch = 2;
export const eventTypeMouse = 3;
export const eventTypeDetected = isNotUndefined(doc[(eventPrefOn + eventPointerDown) as "onpointerdown"])
  ? eventTypePointer
  : isNotUndefined(doc[(eventPrefOn + eventTouchStart) as "ontouchstart"])
    ? eventTypeTouch
    : eventTypeMouse;

export const isTouch = eventTypeDetected === eventTypeTouch;

export const tagA = "a";
export const tagScript = "script";
export const tagCanvas = "canvas";
export const attrStyle = "style";
export const attrSrc = "src";

export function domEventStop(e?: Pick<Event, "stopPropagation" | "preventDefault">) {
  if (e) {
    e.stopPropagation();
    e.preventDefault();
  }
}

export function winAddEvent<E extends Event>(event: string, handler: (e: E) => void) {
  w.addEventListener(event, handler as any);
}

export function winRemoveEvent<E extends Event>(event: string, handler: (e: E) => void) {
  w.removeEventListener(event, handler as any);
}

export function docAddEventListener(et: string, fn: (...a: any[]) => any) {
  domAddEvent(doc, et, fn);
}

export function docRemoveEventListener(et: string, fn: (...a: any[]) => any) {
  domRemoveEvent(doc, et, fn);
}

export function winScrollY() {
  return Math.max(pageYOffset, scrollY, doc.documentElement.scrollTop, doc.body.scrollTop);
}

export function winScrollX() {
  return Math.max(pageXOffset, scrollX, doc.documentElement.scrollLeft, doc.body.scrollLeft);
}

export function getByClass(className: string) {
  return [...doc.getElementsByClassName(className)] as HTMLElement[];
}

export function getById(id: string) {
  return doc.getElementById(id) as HTMLElement | null;
}

export function getFirst(cssRule: string, parent?: HTMLElement | Node) {
  return ((parent as HTMLElement) || doc).querySelector(cssRule) as HTMLElement;
}

export function getAll(cssRule: string, parent?: HTMLElement | Node) {
  return [...((parent as HTMLElement) || doc).querySelectorAll(cssRule)] as HTMLElement[];
}

// ===========

export function removeStorage(key: string) {
  const { localStorage } = w;
  try {
    localStorage.removeItem(key);
  } catch (e) {
  }
}

export function setStorage(key: string, value: string) {
  const { localStorage } = w;
  try {
    localStorage.setItem(key, value);
  } catch (e) {
  }
}

export function getStorage(key: string) {
  const { localStorage } = w;
  try {
    return localStorage.getItem(key);
  } catch (e) {
  }
  return null;
}

// ===========

export function removeSessionStorage(key: string) {
  const { localStorage } = w;
  try {
    localStorage.removeItem(key);
  } catch (e) {
  }
}

export function setSessionStorage(key: string, value: string) {
  const { sessionStorage } = w;
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
  }
}

export function getSessionStorage(key: string) {
  const { sessionStorage } = w;
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
  }
  return null;
}

// ===========

export function documentEnabled() {
  // document.querySelectorAll('meta[name=robots]')
  // <meta name="robots" content="noindex, nofollow, noarchive">
  return !getFirst("meta[name=robots][content^=noindex]");
}

//export function metaRobots() {
//  return document.querySelectorAll("meta[name=robots]").length === 1;
//}

export function scrollToElement(elem: HTMLElement | null) {
  setTimeout(() => {
    if (elem) {
      const top = winScrollY() + (elem.getBoundingClientRect().top - 80);
      w.scrollTo({ top, behavior: "smooth" });
    }
  }, 100);
}

// ===========

export function domElem(tagName: string) {
  return doc.createElement(tagName) as HTMLElement;
}

export function domRemove(elem: HTMLElement) {
  if (elem) {
    const { parentNode } = elem;
    if (parentNode) {
      parentNode.removeChild(elem);
    }
  }
}

export function domParentNode(elem: Node) {
  return elem.parentNode;
}

export function domGetTarget(e: Event) {
  return e.target as Node;
}

export function domHasClass<T extends HTMLElement>(elem: T, className: string) {
  return elem.classList && elem.classList.contains(className);
}

export function domAddClass<T extends Element>(elem: T, className: string | undefined) {
  if (elem.classList && isDefined(className) && className.indexOf(" ") === -1) elem.classList.add(className);
  return elem;
}

export function domRemoveClass<T extends HTMLElement>(elem: T, className: string) {
  if (elem.classList && className.indexOf(" ") === -1) elem.classList.remove(className);
  return elem;
}

export function domNodeName(elem: Node) {
  return elem.nodeName.toLowerCase();
}

export function domGetAttribute(elem: HTMLElement, attrName: string) {
  return elem.getAttribute(attrName);
}

export function domHasAttribute(elem: HTMLElement, attrName: string) {
  return elem.hasAttribute(attrName);
}

export function domRemoveAttribute<T extends HTMLElement>(elem: T, attrName: string) {
  elem.removeAttribute(attrName);
  return elem;
}

export function domSetAttribute<T extends Element>(
  elem: T,
  attrName: string,
  attrVal: boolean | string | number | null | undefined
) {
  if (isDefined(attrVal)) elem.setAttribute(attrName, attrVal.toString());
  return elem;
}

export function domAddEvent<T extends Node>(target: T, event: string, call: <T extends Event>(e: T) => void) {
  target.addEventListener(event, call);
}

export function domRemoveEvent<T extends Node>(target: T, event: string, call: <T extends Event>(e: T) => void) {
  target.removeEventListener(event, call);
}

// ===========

export function isDefined<T>(obj: T | undefined | null): obj is T {
  return isNotUndefined(obj) && obj !== null;
}

export function isNotUndefined<T>(obj: T | undefined): obj is T {
  return obj !== void 0;
}

export function domDropNode(elem: Node) {
  if (!elem) return;
  const parent = elem.parentNode;
  if (parent != null) {
    while (elem.firstChild != null) {
      parent.insertBefore(elem.firstChild, elem);
    }
    parent.removeChild(elem);
  }
}

export function attrClass(elem: HTMLElement) {
  if (elem.hasAttribute("class") && !elem.className) {
    elem.removeAttribute("class");
  }
}

export function loadScriptData(target: HTMLElement) {
  const script = target.firstChild as HTMLScriptElement;
  const results = JSON.parse(script.textContent || "");
  target.innerHTML = "";
  return results;
}
